/*    
<summary>
   This class component is all about Managing AuditLogs data functionality.
   Developer:Aashish Singh, Created Date:22-Nov-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable } from 'mobx';
import { ICommonState } from '../../models/state/ICommonState';
import IApiResponse, { IApiSuccessResponse } from '../../models/response/IApiResponse';
import URLConstants from '../../constants/url.constants';
import * as baseService from '../service/base-service';
import { formatMessage } from '../../translations/format-message';
import toast from 'react-hot-toast';
import moment from 'moment';
import config from '../../helpers/config-helper';
import { initialState as GetAllAuditLogState } from '../initial-state/get-all-audit-logs-state';
import { IAuditLogs, IAuditLogsCSV, IAuditLogsList, IGetAllAuditLogState, ILogsFilter } from '../../models/response/IAuditLogsResponse';
import userActionEnum from '../../constants/user-action-eum';
import actionModuleEnum from '../../constants/action-module-enum';
import { IAuditLogState } from '../../models/state/IAuditLogState';
import { DATE_TIME_FORMAT_FILENAME, emptyValue, infiniteDate } from '../../constants/common-constants';
import { getLanguageFromLocalStorage } from '../../helpers/local-stotrage-helper';

const appConfig = config();
const dateFormat =  appConfig.REACT_APP_DATE_FORMAT;
const dateTimeFormat =  appConfig.REACT_APP_DATE_TIME_FORMAT;

export class AuditLogStore implements IAuditLogState, ICommonState {
    inProgress = false;
    error = '';
    auditLogList: IGetAllAuditLogState = GetAllAuditLogState;
    initialStateValue = {
        success: false,
        error: '',
        inProgress: false
    }
    initialFilter: ILogsFilter = {
        StartDate: moment(new Date(), dateFormat).subtract(7,"days").format(dateFormat),
        EndDate: moment(new Date(), dateFormat).format(dateFormat),
        isFilterActive: false
    }
    logsFilter: ILogsFilter = {
        ...this.initialFilter
    }
    
    auditLog: IAuditLogs | undefined = undefined;
    auditLogState = { ...this.initialStateValue };

    allAuditLogs: Array<IAuditLogs> = [];
    allAuditLogsState = { ...this.initialStateValue };
    constructor() {
        makeObservable(this, {
            inProgress: observable,
            error: observable,
            auditLogList: observable,
            allAuditLogs: observable,
            allAuditLogsState: observable,
            logsFilter: observable,
            auditLog:observable,
            auditLogState:observable,
            GetAuditLogListService: action,
            GetAllAuditLogsService: action,
            GetAuditLogDetailsService: action,
            setLogsFilterDetail: action,
            reset: action,
            resetGetAuditLogDetail: action,
            resetAllAuditLogsState: action,
            resetStore: action,
            getAuditLogList: computed,
            exportAuditLogs: computed,
            auditLogDetail: computed,
            getAllAuditLogs: computed
        });
    }

   /**
    * This function is used to Get AuditLogs list with paging details data from API.
    * @param pageNumber : Current Page Number
    * @param pageSize : Number of records in a page
    * @param filterDetails : Filter information 
    * @returns 
    */
    GetAuditLogListService = (pageNumber: number, pageSize: number, filterDetails: any) => {
        this.inProgress = true;
        const PagingDetails = {
            PageNo: pageNumber,
            PageSize: pageSize,
        }
        const requestBody = {
            StartDate: filterDetails.StartDate + " 00:00:00",
            EndDate: filterDetails.EndDate + " 23:59:59",
        }
        const url = URLConstants.AuditLog;
        return baseService.postRequest(url, { PagingDetails, ...requestBody })
            .then((response: IApiResponse<IApiSuccessResponse<IGetAllAuditLogState>>) => {
                if (response.data.Error) {
                    this.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                }
                else {
                    this.auditLogList = response.data.Data;
                }
            })
            .catch((err: string) => {
                this.error = err;
            })
            .finally(action(() => { this.inProgress = false; }));
    }

    /*
    This function is used to map auditLogList to getAuditLogList List suitable for Grid Component.  
    */
    get getAuditLogList(): IAuditLogsList[] {
        if (this.auditLogList?.Logs && this.auditLogList.Logs?.length > 0)
            return this.auditLogList.Logs.map((AuditLog:IAuditLogs) => {
                let language = getLanguageFromLocalStorage();
                return {
                    Id: AuditLog.Id,
                    User: AuditLog.User,
                    UserName: AuditLog.UserName ? AuditLog.UserName  : emptyValue,
                    Module: actionModuleEnum[AuditLog.Module],
                    Action: userActionEnum[AuditLog.Action],
                    ActionTimestamp: AuditLog.ActionTimestamp ? AuditLog.ActionTimestamp == infiniteDate ?  emptyValue : (moment(AuditLog.ActionTimestamp).format(dateTimeFormat))  
                        : emptyValue,
                    Remarks: AuditLog.Remarks,
                    Language: language
                }
            })
        return [];
    }
   
   GetAllAuditLogsService = (startDate: string, endDate: string, popUpCloseHandler: ()=> void) => {
        this.allAuditLogsState.inProgress = true;
        let StartDate = startDate + " 00:00:00";
        let EndDate = endDate + " 23:59:59";
        const url = `${URLConstants.AllAuditLogs}?StartDate=${StartDate}&EndDate=${EndDate}`;
        return baseService.getRequest(url)
            .then((response: IApiResponse<IApiSuccessResponse<Array<IAuditLogs>>>) => {
                if (response.data.Error) {
                    this.allAuditLogsState.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                }
                else {
                    this.allAuditLogs = response.data.Data;
                    if(response.data.Data && response.data.Data.length > 0) 
                        this.downloadLogsCSV(popUpCloseHandler);
                }
            })
            .catch((err: string) => {
                this.allAuditLogsState.error = err;
                toast.error(formatMessage(err));
            })
            .finally(action(() => { this.allAuditLogsState.inProgress = false; }));
    }

    downloadLogsCSV = (popUpCloseHandler: ()=> void) => {
        const currentDate = new Date();
        const csvData = this.convertToCSV(this.getAllAuditLogs);
        const blob = new Blob([csvData], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          moment(currentDate).format(DATE_TIME_FORMAT_FILENAME) + " Audit_Logs.csv";
        document.body.appendChild(a);
        a.click();
        toast.success(formatMessage("download_started_successfully"));
        document.body.removeChild(a);
        popUpCloseHandler();
      };
    
    convertToCSV = (data: any[]): string => {
        const header = Object.keys(data[0]).join(",");
        const rows = data.map((obj) => Object.values(obj).join(","));
        return `${header}\n${rows.join("\n")}`;
      };

    get getAllAuditLogs(): IAuditLogsCSV[] {
        if (this.allAuditLogs && this.allAuditLogs.length > 0)
            return this.allAuditLogs.map((AuditLog:IAuditLogs) => {
                let language = getLanguageFromLocalStorage();
                return {
                    UserName: AuditLog.UserName ? AuditLog.UserName  : emptyValue,
                    Module: actionModuleEnum[AuditLog.Module],
                    Action: userActionEnum[AuditLog.Action],
                    ActionTimestamp: AuditLog.ActionTimestamp ? AuditLog.ActionTimestamp == infiniteDate ?  emptyValue : (moment(AuditLog.ActionTimestamp).format(dateTimeFormat))  
                        : emptyValue,
                }
            })
        return [];
    }

    resetAllAuditLogsState = () => {
        this.allAuditLogs = [];
        this.allAuditLogsState = { ...this.initialStateValue };
    }
    /*
    This function is used to map auditLogList data to the data suitable for creating CSV.  
    */
    get exportAuditLogs(): IAuditLogsCSV[] {
        if (this.auditLogList?.Logs && this.auditLogList.Logs?.length > 0)
            return this.auditLogList.Logs.map((AuditLog:any) => {
                return {
                    UserName: AuditLog.UserName ? AuditLog.UserName  : emptyValue,
                    Module: actionModuleEnum[AuditLog.Module],
                    Action: userActionEnum[AuditLog.Action],
                    ActionTimestamp: AuditLog.ActionTimestamp ? AuditLog.ActionTimestamp == infiniteDate ?  emptyValue : (moment(AuditLog.ActionTimestamp).format(dateTimeFormat))  
                        : emptyValue,
                }
            })
        return [];
    }
    
    /*
    This function is used to Get audit log detail from API by providing id.  
    */
    GetAuditLogDetailsService = (id: number) => {
        this.auditLogState.inProgress = true;
        const url = URLConstants.AuditLogDetail + "/" + id;
        return baseService.getRequest(url)
            .then((response: IApiResponse<IApiSuccessResponse<IAuditLogs>>) => {
                if (response.data.Error) {
                    this.auditLogState.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                }
                else {
                    this.auditLog = response.data.Data;
                }
            })
            .catch((err: string) => {
                toast.error(formatMessage(err));
                this.auditLogState.error = err;
            })
            .finally(action(() => { this.auditLogState.inProgress = false; }));
    }

    get auditLogDetail():any {
        if (this.auditLog) {
            const actionPerformed = this.auditLog.ActionPerformedOn ? this.auditLog.ActionPerformedOn.replaceAll(",",", ") : "";
            return {
                Id: this.auditLog.Id,
                UserName: this.auditLog?.UserName,
                UserEmail: this.auditLog?.UserEmail,
                Module: actionModuleEnum[this.auditLog.Module],
                Action: userActionEnum[this.auditLog.Action],
                ActionTimestamp: this.auditLog?.ActionTimestamp,
                ActionPerformedOn: actionPerformed,
                Remarks: this.auditLog?.Remarks,
                RemarksType: this.auditLog?.RemarksType,
            }
        }
        return undefined;
    }

    /**
     * This function is used to reset get auditLog observables to their initial values.
     * @returns
     */
    resetGetAuditLogDetail = () => {
        this.auditLog = undefined;
        this.auditLogState = { ...this.initialStateValue }
    }

    /*
    This function is used to reset all observables to their initial values.  
    */

    reset = () => {
        this.error = '';
        this.inProgress = false;
    }

    /**
     * This function is used to reset all store observables to their initial values.
     * @returns
     */
    resetStore = () => {
        this.error = '';
        this.inProgress = false;
        this.auditLogList = GetAllAuditLogState;
        this.logsFilter = {...this.initialFilter}
        this.auditLog = undefined;
        this.auditLogState = {...this.initialStateValue}
    }
    
    /*
    This function is used to set the filter details to the billingFilter observable.  
    */
    setLogsFilterDetail = (filter: ILogsFilter) => {
        this.logsFilter = {...filter};
    }
}

export default new AuditLogStore();
